import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import type { Tenant } from "@repo/shared";

import { api, errorMessage } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

export function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [name, setName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.api.admin.tenants.$get();
      if (!res.ok) throw new Error(await errorMessage(res, "Mandanten konnten nicht geladen werden"));
      const data = await res.json();
      setTenants(data.tenants);
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const res = await api.api.admin.tenants.$post({
        json: {
          name: name.trim(),
          contactEmail: contactEmail.trim() === "" ? null : contactEmail.trim(),
        },
      });
      if (!res.ok) throw new Error(await errorMessage(res, "Mandant konnte nicht angelegt werden"));

      setName("");
      setContactEmail("");
      setError(null);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Anlegen fehlgeschlagen");
    }
  }

  async function toggleStatus(tenant: Tenant) {
    const next = tenant.status === "active" ? "suspended" : "active";
    const res = await api.api.admin.tenants[":id"].status.$put({
      param: { id: tenant.id },
      json: { status: next },
    });
    if (!res.ok) {
      setError(await errorMessage(res, "Status konnte nicht geändert werden"));
      return;
    }
    await load();
  }

  return (
    <section>
      <h1>Mandanten</h1>
      <p className="muted">
        Jeder Mandant bekommt eigene API-Keys. Gesperrte Mandanten werden an der
        öffentlichen API mit <code>403</code> abgewiesen.
      </p>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p className="muted">Lade Mandanten …</p>
      ) : tenants.length === 0 ? (
        <p className="muted">Noch keine Mandanten angelegt.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Mandant</th>
              <th>Slug</th>
              <th>Status</th>
              <th className="num">Limit/min</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => (
              <tr key={tenant.id}>
                <td>
                  <Link to={`/tenants/${tenant.id}`}>{tenant.name}</Link>
                  {tenant.contactEmail && <div className="muted small">{tenant.contactEmail}</div>}
                </td>
                <td>
                  <code>{tenant.slug}</code>
                </td>
                <td>
                  <StatusBadge status={tenant.status} />
                </td>
                <td className="num">{tenant.rateLimitPerMinute}</td>
                <td className="actions">
                  <button type="button" onClick={() => void toggleStatus(tenant)}>
                    {tenant.status === "active" ? "Sperren" : "Entsperren"}
                  </button>
                  <Link className="button" to={`/tenants/${tenant.id}`}>
                    Verwalten
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Mandant anlegen</h2>
      <form className="form" onSubmit={handleCreate}>
        <input
          required
          minLength={2}
          placeholder="Firmenname"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="email"
          placeholder="Kontakt-E-Mail (optional)"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
        />
        <button type="submit" className="primary">
          Anlegen
        </button>
      </form>
    </section>
  );
}
